import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Loader2, Gavel, ClipboardCheck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const COHORT = {
  live: { label: 'LIVE', color: C.red, hint: 'Measurable against the register — a human must dispose of it' },
  legacy_pre_go_live: { label: 'LEGACY · PRE-REGISTER', color: C.blue, hint: 'Notified before the outbound rail existed' },
  legacy_closed: { label: 'LEGACY · CLOSED', color: C.slate, hint: 'File already closed when the register went live' },
};
const WORST = { red: C.red, amber: C.amber, legacy: C.blue, pending: C.slate, ok: C.green };
const CHANNELS = ['email', 'phone', 'portal', 'letter', 'in person'];

function Chip({ text, color, testid, title }) {
  return <span data-testid={testid} title={title} className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide whitespace-nowrap"
    style={{ background: color + '15', color }}>{text}</span>;
}

function Stat({ label, value, color, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-2xl font-bold" style={{ color }}>{value}</p>
      <p className="text-[10px] uppercase leading-tight" style={{ color: C.slate }}>{label}</p>
    </div>
  );
}

function TriageDialog({ api, row, dispositions, onDone, onClose }) {
  const [disposition, setDisposition] = useState('');
  const [reason, setReason] = useState('');
  const [ev, setEv] = useState({ contacted_at: '', channel: 'email', note: '' });
  const [saving, setSaving] = useState(false);
  const spec = dispositions.find(d => d.id === disposition);
  const submit = async () => {
    setSaving(true);
    try {
      const body = { disposition, reason, supersede: !!row.triage };
      if (spec?.needs_evidence) body.evidence = ev;
      const { data } = await api.post(`/part-f/triage/${row.claim_id}`, body);
      toast.success(`${data.triage.label} recorded — ${data.claim_closed ? 'claim closed, ' : ''}register now ${data.part_f.worst.toUpperCase()}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Triage failed'); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="triage-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm" style={{ color: C.navy }}>
            Triage {row.claim_reference} · {row.business_name}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px]" style={{ color: C.slate }}>
          {row.flags?.map(f => `${f.id} ${f.status.toUpperCase()} — ${f.promise}`).join(' · ') || 'No open flags'}
        </p>
        {row.triage && (
          <p className="text-[11px]" style={{ color: '#B27516' }}>
            Already disposed: {row.triage.label} by {row.triage.by} on {String(row.triage.at).slice(0, 10)} — this records a superseding disposition and keeps the first.
          </p>
        )}
        <Select value={disposition} onValueChange={setDisposition}>
          <SelectTrigger className="text-xs h-8 rounded-sm" data-testid="triage-disposition-select">
            <SelectValue placeholder="Choose a disposition" />
          </SelectTrigger>
          <SelectContent>
            {dispositions.map(d => (
              <SelectItem key={d.id} value={d.id} className="text-xs" data-testid={`triage-option-${d.id}`}>{d.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {spec && <p className="text-[11px]" style={{ color: C.slate }}>{spec.desc}.{spec.closes_claim ? ' The claim is closed at triage.' : ''}</p>}
        {spec?.needs_evidence && (
          <div className="grid grid-cols-2 gap-1.5">
            <Input type="date" value={ev.contacted_at} onChange={e => setEv(v => ({ ...v, contacted_at: e.target.value }))}
              className="text-xs rounded-sm h-8" data-testid="triage-evidence-date" />
            <Select value={ev.channel} onValueChange={v => setEv(x => ({ ...x, channel: v }))}>
              <SelectTrigger className="text-xs h-8 rounded-sm" data-testid="triage-evidence-channel"><SelectValue /></SelectTrigger>
              <SelectContent>{CHANNELS.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="What was said / where it is filed" value={ev.note} onChange={e => setEv(v => ({ ...v, note: e.target.value }))}
              className="text-xs rounded-sm h-8 col-span-2" data-testid="triage-evidence-note" />
          </div>
        )}
        <Textarea placeholder="Written reason — this is the line a capacity provider reads" value={reason}
          onChange={e => setReason(e.target.value)} rows={3} className="text-xs rounded-sm" data-testid="triage-reason-input" />
        <DialogFooter>
          <Button size="sm" variant="ghost" onClick={onClose} className="text-xs" data-testid="triage-cancel-btn">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={saving || !disposition} className="text-xs rounded-sm"
            style={{ background: C.navy }} data-testid="triage-submit-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Record disposition'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const PartFTriageCard = ({ onChanged }) => {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [q, setQ] = useState(null);
  const [filters, setFilters] = useState({ cohort: 'all', triaged: 'untriaged' });
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 25 };
    if (filters.cohort !== 'all') params.cohort = filters.cohort;
    if (filters.triaged !== 'all') params.triaged = filters.triaged;
    api.get('/part-f/triage-queue', { params })
      .then(({ data }) => setQ(data))
      .catch(() => toast.error('Failed to load the triage queue'))
      .finally(() => setLoading(false));
  }, [api, filters]);

  useEffect(() => { load(); }, [load]);

  const runSweep = async (dry) => {
    setSweeping(true);
    try {
      const { data } = await api.post(`/part-f/legacy-sweep?dry_run=${dry}`);
      if (dry) { setPreview(data); } else {
        setPreview(null);
        toast.success(data.note);
        load();
        onChanged?.();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Sweep failed'); }
    setSweeping(false);
  };

  const counts = q?.counts || {};
  return (
    <Card className="rounded-md bg-white p-4" data-testid="legacy-triage-card"
      style={{ border: '1px solid rgba(136,150,168,0.12)', borderLeft: `3px solid ${counts.live_action_required ? C.red : C.green}` }}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Gavel className="w-4 h-4" style={{ color: C.navy }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.slate }}>Legacy claim triage — every red row explained or closed</h3>
        </div>
        <div className="flex gap-1.5 items-center">
          <Select value={filters.cohort} onValueChange={v => setFilters(f => ({ ...f, cohort: v }))}>
            <SelectTrigger className="text-xs h-7 w-44 rounded-sm" data-testid="triage-cohort-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All cohorts</SelectItem>
              <SelectItem value="live" className="text-xs">Live — action required</SelectItem>
              <SelectItem value="legacy_pre_go_live" className="text-xs">Legacy — pre-register</SelectItem>
              <SelectItem value="legacy_closed" className="text-xs">Legacy — closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.triaged} onValueChange={v => setFilters(f => ({ ...f, triaged: v }))}>
            <SelectTrigger className="text-xs h-7 w-36 rounded-sm" data-testid="triage-state-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="untriaged" className="text-xs">Needs a decision</SelectItem>
              <SelectItem value="triaged" className="text-xs">Already explained</SelectItem>
              <SelectItem value="all" className="text-xs">Everything</SelectItem>
            </SelectContent>
          </Select>
          <button onClick={load} className="p-1 rounded hover:bg-slate-50" data-testid="triage-refresh" style={{ color: C.slate }}><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      <div className="flex gap-5 flex-wrap mb-3">
        <Stat label="on the register" value={counts.total ?? 0} color={C.navy} testid="triage-count-total" />
        <Stat label="live — action required" value={counts.live_action_required ?? 0} color={C.red} testid="triage-count-live" />
        <Stat label="legacy · pre-register" value={counts.legacy_pre_go_live ?? 0} color={C.blue} testid="triage-count-pre" />
        <Stat label="legacy · closed" value={counts.legacy_closed ?? 0} color={C.slate} testid="triage-count-closed" />
        <Stat label="explained & signed off" value={counts.explained ?? 0} color={C.green} testid="triage-count-explained" />
        <Stat label="test artifacts closed" value={counts.test_artifact ?? 0} color={C.slate} testid="triage-count-test" />
        <Stat label="breaches accepted" value={counts.breach_accepted ?? 0} color={C.amber} testid="triage-count-breach" />
      </div>

      {isAdmin && (counts.untriaged ?? 0) > 0 && (
        <div className="mb-3 p-2 rounded-sm" style={{ background: '#F4F7FA' }}>
          {!preview ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" onClick={() => runSweep(true)} disabled={sweeping} className="text-xs rounded-sm" style={{ background: C.blue }} data-testid="legacy-sweep-preview-btn">
                {sweeping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Preview the legacy sweep'}
              </Button>
              <span className="text-[11px]" style={{ color: C.slate }}>
                Records the deterministic disposition against every legacy row and closes platform test artifacts — signed in your name, audit-logged, nothing deleted. Live reds are never auto-explained.
              </span>
            </div>
          ) : (
            <div className="flex items-start gap-2 flex-wrap" data-testid="legacy-sweep-preview">
              <ClipboardCheck className="w-4 h-4 mt-0.5" style={{ color: C.blue }} />
              <div className="flex-1 min-w-[16rem]">
                <p className="text-[11px]" style={{ color: C.navy }}>
                  <b>{preview.closed_before_measurement}</b> closed before measurement · <b>{preview.pre_register_no_rail}</b> pre-register (no rail) · <b>{preview.test_artifact}</b> platform test artifacts · <b>{preview.live_action_required}</b> live reds untouched · {preview.re_evaluated} claims re-measured.
                </p>
                <p className="text-[10px]" style={{ color: C.slate }}>{preview.note}</p>
              </div>
              <Button size="sm" onClick={() => runSweep(false)} disabled={sweeping} className="text-xs rounded-sm" style={{ background: C.navy }} data-testid="legacy-sweep-apply-btn">
                {sweeping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Apply & sign off'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPreview(null)} className="text-xs" data-testid="legacy-sweep-discard-btn">Discard</Button>
            </div>
          )}
        </div>
      )}

      {loading && !q ? <p className="text-xs" style={{ color: C.slate }}>Loading the triage queue…</p> : (
        <>
          <table className="w-full text-xs" data-testid="triage-queue-table">
            <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
              <th className="py-1 pr-2">Claim</th><th className="py-1 pr-2">Insured</th><th className="py-1 pr-2">FNOL</th>
              <th className="py-1 pr-2">Flag</th><th className="py-1 pr-2">Cohort</th><th className="py-1 pr-2">Explanation</th><th className="py-1"></th>
            </tr></thead>
            <tbody>
              {(q?.rows || []).map(r => (
                <tr key={r.claim_id} className="border-t align-top" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`triage-row-${r.claim_reference}`}>
                  <td className="py-1.5 pr-2 font-mono text-[10px]" style={{ color: C.slate }}>{r.claim_reference}</td>
                  <td className="py-1.5 pr-2 font-medium" style={{ color: C.navy }}>{r.business_name}<span className="ml-1 text-[10px] font-normal" style={{ color: C.slate }}>{r.claim_type}</span></td>
                  <td className="py-1.5 pr-2" style={{ color: C.slate }}>{r.date_reported}<span className="ml-1 text-[10px]">{r.days_since_fnol}d</span></td>
                  <td className="py-1.5 pr-2"><Chip text={r.worst} color={WORST[r.worst] || C.slate} testid={`triage-worst-${r.claim_reference}`} /></td>
                  <td className="py-1.5 pr-2"><Chip text={COHORT[r.cohort]?.label || r.cohort} color={COHORT[r.cohort]?.color || C.slate} title={COHORT[r.cohort]?.hint} /></td>
                  <td className="py-1.5 pr-2 text-[11px]" style={{ color: r.triage ? C.navy : '#B03230' }}>
                    {r.triage ? (
                      <>
                        <b>{r.triage.label}</b> — {r.triage.reason}
                        <span className="block text-[10px]" style={{ color: C.slate }}>{r.triage.by} · {String(r.triage.at).slice(0, 10)}{r.triage.automatic ? ' · sweep' : ''}</span>
                      </>
                    ) : (r.hint || r.flags?.[0]?.detail || r.flags?.map(f => `${f.id} ${f.status}`).join(', ') || 'Needs a decision')}
                  </td>
                  <td className="py-1.5 text-right">
                    {isAdmin && (
                      <Button size="sm" variant="outline" onClick={() => setActive(r)} className="text-[10px] h-6 rounded-sm px-2" data-testid={`triage-btn-${r.claim_reference}`}>
                        {r.triage ? 'Supersede' : 'Triage'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!(q?.rows || []).length && <p className="text-xs py-3" style={{ color: C.green }} data-testid="triage-queue-empty">Nothing in this view — every row is explained or closed.</p>}
          <p className="text-[10px] mt-2" style={{ color: C.slate }}>
            Showing {(q?.rows || []).length} of {q?.total ?? 0} rows in this filter. The register measures from {q?.measured_from} — the two legacy cohorts are disclosed, never counted as promises kept.
          </p>
        </>
      )}

      {active && (
        <TriageDialog api={api} row={active} dispositions={q?.dispositions || []}
          onClose={() => setActive(null)}
          onDone={() => { setActive(null); load(); onChanged?.(); }} />
      )}
    </Card>
  );
};
